import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import AppEnv from "@/config/env";

export interface DriverOrderImage {
  imageId: number;
  orderSlipCode: string;
  imageType: string; // LOADING, UNLOADING_1, UNLOADING_2, UNLOADING_3
  originalFileName: string;
  storedFileName: string;
  filePath: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  driverCode: string;
}

export const driverOrderImageQueryKey = "driverOrderImage";

/**
 * 주문별 이미지 목록 조회
 */
export const useDriverOrderImages = (orderSlipCode: string, options?: { enabled?: boolean }) => {
  return useQuery<DriverOrderImage[]>({
    queryKey: [driverOrderImageQueryKey, orderSlipCode],
    queryFn: async () => {
      const res = await api.get(`/driver-order/images/list/${orderSlipCode}`);
      // api.get은 이미 res.data를 반환하므로 res.data가 ApiResponse 형태
      return res.data || [];
    },
    enabled: options?.enabled !== false && !!orderSlipCode,
  });
};

/**
 * 이미지 업로드
 */
export const useUploadDriverOrderImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      orderSlipCode,
      imageType,
      driverCode,
    }: {
      file: File;
      orderSlipCode: string;
      imageType: string;
      driverCode?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("orderSlipCode", orderSlipCode);
      formData.append("imageType", imageType);
      if (driverCode) {
        formData.append("driverCode", driverCode);
      }

      const res = await api.post("/driver-order/images/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [driverOrderImageQueryKey, variables.orderSlipCode],
      });
    },
  });
};

/**
 * 이미지 삭제
 */
export const useDeleteDriverOrderImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ imageId, orderSlipCode }: { imageId: number; orderSlipCode: string }) => {
      await api.delete(`/driver-order/images/${imageId}`);
      return { imageId, orderSlipCode };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [driverOrderImageQueryKey, variables.orderSlipCode],
      });
    },
  });
};

/**
 * 이미지 다운로드 URL 생성
 */
export const getDriverOrderImageUrl = (imageId: number): string => {
  // AppEnv.API_URL은 이미 /api를 포함하고 있음 (예: http://localhost:9003/api)
  return `${AppEnv.API_URL}/driver-order/images/download/${imageId}`;
};
